"use client"

import { useState, useRef, useEffect } from "react"
import { Modal } from "@/components/ui/modal"
import { Button } from "@/components/ui/button"
import { Folder, Upload, X, Trash2, RefreshCw, Loader2 } from "lucide-react"
import Image from "next/image"
import { useCreateFolderMutation, useUpdateFolderMutation, type Folder as FolderType } from "@/app/store/api/pitchDeckApi"
import { toast } from "sonner"

interface CreateFolderModalProps {
    isOpen: boolean
    onClose: () => void
    parentFolderId?: string
    folder?: FolderType
}

export function CreateFolderModal({ isOpen, onClose, parentFolderId, folder }: CreateFolderModalProps) {
    const [folderName, setFolderName] = useState(folder?.name || "")
    const [iconFile, setIconFile] = useState<File | null>(null)
    const [iconPreview, setIconPreview] = useState<string | null>(folder?.logo || null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [createFolder, { isLoading: isCreating }] = useCreateFolderMutation()
    const [updateFolder, { isLoading: isUpdating }] = useUpdateFolderMutation()

    const isLoading = isCreating || isUpdating

    // Reset state when modal closes/opens
    useEffect(() => {
        if (isOpen) {
            setFolderName(folder?.name || "")
            setIconPreview(folder?.logo || null)
            setIconFile(null)
        } else {
            setFolderName("")
            handleRemoveIcon()
        }
    }, [isOpen, folder])

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            // Cleanup previous object URL to avoid memory leaks
            if (iconPreview) {
                URL.revokeObjectURL(iconPreview)
            }

            const previewUrl = URL.createObjectURL(file)
            setIconFile(file)
            setIconPreview(previewUrl)
        }
    }

    const handleRemoveIcon = () => {
        if (iconPreview) {
            URL.revokeObjectURL(iconPreview)
        }
        setIconFile(null)
        setIconPreview(null)
        if (fileInputRef.current) {
            fileInputRef.current.value = ""
        }
    }

    const handleTriggerUpload = () => {
        fileInputRef.current?.click()
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!folderName.trim()) {
            toast.error("Please enter a folder name")
            return
        }

        try {
            if (folder?._id) {
                await updateFolder({
                    folderId: folder._id,
                    name: folderName.trim(),
                    logo: iconFile || undefined,
                }).unwrap()
                toast.success("Folder updated successfully")
            } else {
                await createFolder({
                    name: folderName.trim(),
                    parentFolderId: parentFolderId,
                    logo: iconFile || undefined,
                }).unwrap()
                toast.success("Folder created successfully")
            }

            onClose()
        } catch (error: any) {
            toast.error(error?.data?.message || `Failed to ${folder?._id ? "update" : "create"} folder`)
        }
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="max-w-md">
            <div className="p-6">
                {/* Header */}
                <div className="mb-6">
                    <h2 className="text-xl font-bold text-slate-900">
                        {folder ? "Edit Folder" : "Create New Folder"}
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                        {folder ? "Update your folder details" : "Organize your projects efficiently"}
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Folder Name Input */}
                    <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-700">
                            Folder Name
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                value={folderName}
                                onChange={(e) => setFolderName(e.target.value)}
                                placeholder="e.g. Marketing Decks"
                                maxLength={20}
                                className="w-full mt-1 px-4 py-2.5 rounded-lg border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all pr-16"
                                autoFocus
                            />
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 text-[10px] font-medium text-slate-400">
                                {folderName.length}/15
                            </div>
                        </div>
                    </div>

                    {/* Folder Icon Section */}
                    <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-700">
                            Folder Icon <span className="text-slate-400 font-normal">(Optional)</span>
                        </label>
                        <div className="flex gap-4 items-start pt-1">
                            {/* Icon Preview Box */}
                            <div className="relative w-20 h-20 rounded-xl bg-slate-50 border-2 border-slate-200/60 border-dashed flex items-center justify-center overflow-hidden shrink-0 group">
                                {iconPreview ? (
                                    <>
                                        <Image
                                            src={iconPreview}
                                            alt="Folder Icon"
                                            fill
                                            className="object-cover"
                                        />
                                        {/* Hover Overlay for quick delete */}
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <button
                                                type="button"
                                                onClick={handleRemoveIcon}
                                                className="text-white bg-red-500/80 p-1.5 rounded-full hover:bg-red-600 transition-colors"
                                                title="Remove Icon"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </>
                                ) : (
                                    <Folder className="w-8 h-8 text-slate-300" strokeWidth={1.5} />
                                )}
                            </div>

                            {/* Upload Actions */}
                            <div className="flex flex-col gap-2 pt-1">
                                <div className="flex gap-2">
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        className="hidden"
                                        accept="image/png, image/jpeg, image/jpg"
                                        onChange={handleFileSelect}
                                    />

                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={handleTriggerUpload}
                                        className="bg-blue-50 text-blue-600 border-blue-100 hover:bg-blue-100 hover:border-blue-200 hover:text-blue-700 w-fit gap-2"
                                    >
                                        {iconPreview ? (
                                            <>
                                                <RefreshCw className="w-3.5 h-3.5" />
                                                Replace Icon
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="w-3.5 h-3.5" />
                                                Upload Icon
                                            </>
                                        )}
                                    </Button>

                                    {iconPreview && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={handleRemoveIcon}
                                            className="text-red-500 border-red-100 bg-red-50 hover:bg-red-100 hover:text-red-600 hover:border-red-200 w-fit gap-2"
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                            Delete
                                        </Button>
                                    )}
                                </div>

                                <p className="text-xs text-slate-400 max-w-[200px] leading-relaxed">
                                    Recommended: Square PNG or JPG, max 5MB.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Footer Actions */}
                    <div className="grid grid-cols-2 gap-3 pt-4 border-t border-slate-50 mt-8">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={onClose}
                            disabled={isLoading}
                            className="text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isLoading || !folderName.trim()}
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    {folder ? "Updating..." : "Creating..."}
                                </>
                            ) : (
                                folder ? "Update Folder" : "Create Folder"
                            )}
                        </Button>
                    </div>
                </form>
            </div>
        </Modal>
    )
}
